<script setup lang="ts">
import { z } from 'zod'

const emit = defineEmits<{
  submit: [data: {
    name: string
    email: string
    phone: string
    company: string
    notes: string
  }]
}>()

const loading = defineModel<boolean>('loading', { default: false })

// Simplified form schema - only 3 required fields
const schema = z.object({
  name: z.string().min(2, 'Ingrese su nombre'),
  phone: z.string().min(10, 'Ingrese un telefono valido'),
  email: z.string().email('Ingrese un email valido'),
  company: z.string().optional(),
  notes: z.string().optional()
})

type FormData = z.infer<typeof schema>

const state = reactive<FormData>({
  name: '',
  phone: '',
  email: '',
  company: '',
  notes: ''
})

const handleSubmit = () => {
  emit('submit', {
    name: state.name,
    phone: state.phone,
    email: state.email,
    company: state.company || '',
    notes: state.notes || ''
  })
}
</script>

<template>
  <UForm :schema="schema" :state="state" @submit="handleSubmit">
    <div class="space-y-6">
      <!-- Progress Indicator -->
      <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-3 text-sm">
          <div class="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center font-semibold">
            2
          </div>
          <div>
            <p class="font-medium text-gray-900 dark:text-white">Paso 2: Sus datos de contacto</p>
            <p class="text-xs text-gray-600 dark:text-gray-400">Complete solo 3 campos requeridos</p>
          </div>
        </div>
      </div>

      <!-- Required Fields Section -->
      <div class="space-y-5">
        <div class="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
          <UIcon name="i-heroicons-user-circle" class="w-5 h-5 text-primary-500" />
          <h3 class="font-semibold text-gray-900 dark:text-white">Informacion basica</h3>
          <span class="ml-auto text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 px-2 py-1 rounded">
            Requerido
          </span>
        </div>

        <!-- Name Field - Larger touch target -->
        <UFormField label="Nombre completo" name="name" required>
          <UInput
            v-model="state.name"
            placeholder="Ej: Juan Perez"
            icon="i-heroicons-user"
            size="xl"
            :disabled="loading"
            class="text-lg"
          />
        </UFormField>

        <!-- Phone Field - Larger touch target -->
        <UFormField label="Telefono" name="phone" required>
          <UInput
            v-model="state.phone"
            type="tel"
            placeholder="Ej: 55 9296 0319"
            icon="i-heroicons-phone"
            size="xl"
            :disabled="loading"
            class="text-lg"
          />
          <template #hint>
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Lo contactaremos por WhatsApp o llamada
            </span>
          </template>
        </UFormField>

        <!-- Email Field - Larger touch target -->
        <UFormField label="Email" name="email" required>
          <UInput
            v-model="state.email"
            type="email"
            placeholder="Ej: juan@construccion.com"
            icon="i-heroicons-envelope"
            size="xl"
            :disabled="loading"
            class="text-lg"
          />
          <template #hint>
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Recibira la cotizacion en PDF por email
            </span>
          </template>
        </UFormField>
      </div>

      <!-- Optional Fields Section -->
      <div class="space-y-5">
        <div class="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
          <UIcon name="i-heroicons-building-office" class="w-5 h-5 text-gray-400" />
          <h3 class="font-semibold text-gray-900 dark:text-white">Informacion adicional</h3>
          <span class="ml-auto text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded">
            Opcional
          </span>
        </div>

        <UFormField label="Empresa o proyecto" name="company">
          <UInput
            v-model="state.company"
            placeholder="Construcciones ABC (opcional)"
            icon="i-heroicons-building-office"
            size="lg"
            :disabled="loading"
          />
        </UFormField>

        <UFormField label="Comentarios especiales" name="notes">
          <UTextarea
            v-model="state.notes"
            placeholder="Ej: Necesito entrega urgente, descuento por volumen, etc. (opcional)"
            :rows="3"
            :disabled="loading"
            size="lg"
            class="text-base"
          />
        </UFormField>
      </div>

      <!-- Trust Signals -->
      <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
        <div class="flex items-start gap-3">
          <UIcon name="i-heroicons-shield-check" class="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div class="text-sm">
            <p class="font-medium text-green-900 dark:text-green-100 mb-1">Sus datos estan seguros</p>
            <ul class="text-xs text-green-700 dark:text-green-300 space-y-1">
              <li class="flex items-center gap-1">
                <UIcon name="i-heroicons-check" class="w-3 h-3" />
                No compartimos su informacion
              </li>
              <li class="flex items-center gap-1">
                <UIcon name="i-heroicons-check" class="w-3 h-3" />
                Respuesta en menos de 24 horas
              </li>
              <li class="flex items-center gap-1">
                <UIcon name="i-heroicons-check" class="w-3 h-3" />
                Precios competitivos garantizados
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Large Submit Button -->
      <UButton
        type="submit"
        color="primary"
        size="xl"
        class="w-full h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
        :loading="loading"
        :disabled="loading"
      >
        <template v-if="!loading">
          <UIcon name="i-heroicons-paper-airplane" class="w-6 h-6 mr-2" />
          Enviar Solicitud de Cotizacion
        </template>
      </UButton>

      <!-- What happens next -->
      <div class="text-center">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Que sucede despues?
        </p>
        <div class="flex justify-center gap-4 text-xs text-gray-600 dark:text-gray-400">
          <div class="flex items-center gap-1">
            <span class="font-semibold text-primary-600 dark:text-primary-400">1.</span>
            Recibe PDF
          </div>
          <div class="flex items-center gap-1">
            <span class="font-semibold text-primary-600 dark:text-primary-400">2.</span>
            Revisa precios
          </div>
          <div class="flex items-center gap-1">
            <span class="font-semibold text-primary-600 dark:text-primary-400">3.</span>
            Paga en linea
          </div>
        </div>
      </div>
    </div>
  </UForm>
</template>
